import type { EffectivePriorityCtx, Task } from "@app/core";
import { effectivePriorityOf, effectiveUrgencyOf, PRIORITY_RANK } from "@app/core";

/** Items in the undated backlog: active, non-recurring entries with no `due_at`
 *  that are top-level (not a subtask). Includes BOTH tasks and events (an undated
 *  event still belongs here; it renders with its calendar color). Undated
 *  subtasks surface nested under their parent — here (via {@link inboxChildren})
 *  when the parent is also in the inbox, or inside the parent's block on the
 *  calendar when the parent is scheduled. */
export function tasksForInbox(tasks: ReadonlyArray<Task>): Task[] {
  return tasks.filter(
    (t) =>
      !t.virtual &&
      !t.deleted_at &&
      !t.recurrence_id &&
      !t.due_at &&
      !t.subtask_parent_id,
  );
}

export type InboxSort = "default" | "priority" | "urgency" | "title";

/** Time + settings context so the priority/urgency sorts derive from deadlines
 *  exactly like the sidebar filter — see `@app/core` `EffectivePriorityCtx`. */
export type UrgencySortContext = EffectivePriorityCtx;

/** Order the backlog for display. "default" keeps store order; priority and
 *  urgency put the most pressing first, using the EFFECTIVE (deadline-derived)
 *  level — same rule as the sidebar filter. */
export function sortInboxTasks(tasks: Task[], sort: InboxSort, ctx: UrgencySortContext): Task[] {
  if (sort === "default") return tasks;
  const arr = [...tasks];
  switch (sort) {
    case "priority":
      arr.sort((a, b) => PRIORITY_RANK[effectivePriorityOf(b, ctx)] - PRIORITY_RANK[effectivePriorityOf(a, ctx)]);
      break;
    case "urgency":
      arr.sort((a, b) => PRIORITY_RANK[effectiveUrgencyOf(b, ctx)] - PRIORITY_RANK[effectiveUrgencyOf(a, ctx)]);
      break;
    case "title":
      arr.sort((a, b) => a.title.localeCompare(b.title));
      break;
  }
  return arr;
}

/** Undated subtasks of `parentId`, for nested display inside an inbox row. */
export function inboxChildren(tasks: ReadonlyArray<Task>, parentId: string): Task[] {
  return tasks.filter(
    (t) => !t.virtual && !t.deleted_at && !t.due_at && t.subtask_parent_id === parentId,
  );
}
